/**
 * Validation for an OPTIONAL link out of this app, to a system that may not
 * exist yet. Its own module rather than a helper inside appConfig.ts so it can
 * be tested without evaluating appConfig, which reads import.meta.env and
 * throws on a missing required var at module load.
 */

/**
 * True for a host that can only mean "inside our own network", where plain
 * HTTP is the normal state of affairs rather than a mistake:
 *
 *   - localhost and the loopback range
 *   - a private IPv4 range (RFC 1918)
 *   - a single-label name with no dot at all, e.g. RECEIVING-PC, which a
 *     public DNS resolver cannot answer and only a LAN can
 *   - a .local name (mDNS)
 *
 * Deliberately NOT a general "is this http allowed" escape hatch. A public
 * hostname still has to be https, because a login form posted over plain HTTP
 * across the internet is a different category of problem from one posted
 * across an office switch.
 */
function isPrivateNetworkHost(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/^\[|\]$/g, "");

  if (h === "localhost" || h.endsWith(".localhost")) return true;
  if (h === "::1") return true;
  if (h.endsWith(".local")) return true;

  const v4 = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (v4) {
    const [a, b] = [Number(v4[1]), Number(v4[2])];
    if (a === 10 || a === 127) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
    return false; // a public IP literal is not a LAN host
  }

  // No dot anywhere means no public DNS suffix, so it cannot resolve off-LAN.
  // Checked last so an IP-shaped string is never mistaken for a short name.
  return !h.includes(".");
}

/**
 * Returns the URL when it is usable as an outbound link, or "" when it is not.
 *
 * "" is what an unset variable, a leftover PASTE_ placeholder and a typo all
 * look like from here. Callers treat "" as "not stood up yet" and disable the
 * entry point rather than render a link that goes nowhere: a dead tile is
 * worse than one that says SOON, because the person clicking it cannot tell
 * which of the two it is.
 *
 * A public host must be https. The .app TLD is on the HSTS preload list as a
 * whole TLD, so every hostname under it is HTTPS-only in every major browser
 * with no click-through, whatever headers the host itself sends. Note that is
 * the reason and this app's own includeSubDomains header is not: that header
 * covers subdomains OF i-synergy.intalgarmentsopc.app, not siblings of it. An
 * http:// value for such a host cannot load and fails with nothing on screen
 * explaining why, so rejecting it here turns a silent failure into a visible
 * SOON badge.
 *
 * A LAN host may be http, because that is how a server on an office network
 * is actually reached. See isPrivateNetworkHost.
 */
export function optionalExternalUrl(value: string | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw || raw.startsWith("PASTE_")) return "";

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return "";
  }

  if (url.protocol === "https:") return raw;
  if (url.protocol === "http:" && isPrivateNetworkHost(url.hostname)) return raw;
  return "";
}
